import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast'; // ✅ YENİ: Toast notification
import Sidebar from './Sidebar';
import Header from './components/Header';
import { AuthProvider, useAuth } from './context/AuthContext';
import { KompresorProvider } from './context/KompresorContext';
import { TeklifProvider } from './context/TeklifContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import './styles/layout.css';

// Sayfa bileşenleri
import AnaSayfa from './pages/AnaSayfa';
import UrunlerSayfasi from './pages/UrunlerSayfasi';
import KategoriDetay from './pages/KategoriDetay';
import UrunDetay from './pages/UrunDetay';
import AdminPanel from './pages/AdminPanel';
import Dashboard from './pages/Dashboard';
import TeklifGonder from './pages/TeklifGonder';
import StokTakip from './pages/StokTakip';
import BakimPlanlayici from './pages/BakimPlanlayici';
import CustomGantt from './components/CustomGantt';
import RevizyonPlanlama from './pages/RevizyonPlanlama';
import ParcaKatalog from './pages/ParcaKatalog';
import TeklifGecmisi from './pages/TeklifGecmisi';
import TeklifFatura from './pages/TeklifFatura';
import ParcaEsleme from './pages/ParcaEsleme';
import KullaniciYonetimi from './pages/KullaniciYonetimi';
import AramaFiltreleme from './pages/AramaFiltreleme';
import EpostaTeklifGonder from './pages/EpostaTeklifGonder';
import ParcaTahmin from './pages/ParcaTahmin';
import KompresorDetay from './pages/KompresorDetay';
import KompresorListesi from './pages/KompresorListesi';
import TedarikciYonetimi from './pages/TedarikciYonetimi';
import Register from './pages/Register';
import Login from './pages/Login';
import ParasutCallback from './pages/ParasutCallback';
import ParasutFaturaListesi from './pages/ParasutFaturaListesi';
import ParasutFaturaGonder from './pages/ParasutFaturaGonder';
import TeklifBasvurular from './pages/TeklifBasvurular';
import Hizmetler from './components/Hizmetler';
import Iletisim from './pages/Iletisim';
import CompressorPartTracker from './pages/CompressorPartTracker';
import MuadilYonetim from './pages/MuadilYonetim';
import KompresorHesaplayici from './pages/KompresorHesaplayici';
import LupamatKodUretici from './pages/LupamatKodUretici';

// Footer
import Footer from './components/Footer';

// Servis Formu
import ServisFormu from './pages/ServisFormu';

// ✅ GELİŞTİRİLMİŞ SessionTimeout — AuthContext kullanıyor
const SessionTimeout = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const timeoutRef = useRef(null);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (user) {
        logout();
        navigate('/login', {
          replace: true,
          state: { reason: 'timeout' }
        });
      }
    }, 60 * 60 * 1000);
  }, [user, logout, navigate]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [user, resetTimer]);

  return null;
};

// ✅ AppRoutes — useAuth() kullanıyor
const AppRoutes = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    document.body.setAttribute(
      'data-page',
      location.pathname === '/' ? 'hero' : 'other'
    );
  }, [location.pathname]);

  const moonxRotalari = [
    '/dashboard',
    '/kompresor-detay',
    '/kompresor-kayitlari',
    '/stok-takip',
    '/parca-katalog',
    '/revizyon-planlama',
    '/parca-esleme',
    '/teklif-fatura',
    '/teklif-gonder',
    '/teklif-gecmisi',
    '/kullanici-yonetimi',
    '/arama-filtre',
    '/parasut-fatura-gonder',
    '/parasut-faturalar',
    '/admin-panel',
    '/teklif-basvurular',
    '/bakim-planlayici',
    '/bakim-gantt',
    '/compressor-yonetimi',
    '/tedarikci-yonetimi',
    '/eposta-teklif',
    '/parca-tahmin',
    '/servis-form',
    '/muadil-yonetim',
    '/kompresor-hesaplayici',
    '/lupamat-kod-uretici'
  ];

  const isMoonx = moonxRotalari.some((rota) =>
    location.pathname === rota || location.pathname.startsWith(`${rota}/`)
  );

  const isLoginOrRegister = location.pathname === '/login' || location.pathname === '/register';
  const showHeader = !isMoonx && !isLoginOrRegister;
  const showFooter = !isLoginOrRegister && !isMoonx && location.pathname !== '/parasut-callback';

  return (
    <>
      {showHeader && (
        <Header
          onTeklifTikla={() => {
            if (location.pathname === '/') {
              window.dispatchEvent(new Event('teklifTikla'));
            }
          }}
        />
      )}

      <div className="layout-wrapper">
        {user && isMoonx && <Sidebar />}

        <main className={`layout-content ${user && isMoonx ? 'with-sidebar' : 'full'}`}>
          <Routes>
            <Route path="/" element={<AnaSayfa />} />
            <Route path="/urunler" element={<UrunlerSayfasi />} />
            <Route path="/urunler/:kategori" element={<KategoriDetay />} />
            <Route path="/urun/:id" element={<UrunDetay />} />
            <Route path="/hizmetler" element={<Hizmetler />} />
            <Route path="/iletisim" element={<Iletisim />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<ProtectedRoute allowedRoles={['admin']}><Register /></ProtectedRoute>} />
            <Route path="/parasut-callback" element={<ParasutCallback />} />
            <Route path="/servis-form" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen']}><ServisFormu /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/teklif-gonder" element={<ProtectedRoute allowedRoles={['admin', 'satış']}><TeklifGonder /></ProtectedRoute>} />
            <Route path="/stok-takip" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen']}><StokTakip /></ProtectedRoute>} />
            <Route path="/bakim-planlayici" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen']}><BakimPlanlayici /></ProtectedRoute>} />
            <Route path="/bakim-gantt" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen']}><CustomGantt /></ProtectedRoute>} />
            <Route path="/revizyon-planlama" element={<ProtectedRoute allowedRoles={['admin']}><RevizyonPlanlama /></ProtectedRoute>} />
            <Route path="/parca-katalog" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen', 'satış']}><ParcaKatalog /></ProtectedRoute>} />
            <Route path="/teklif-gecmisi" element={<ProtectedRoute allowedRoles={['admin', 'satış']}><TeklifGecmisi /></ProtectedRoute>} />
            <Route path="/teklif-fatura" element={<ProtectedRoute allowedRoles={['admin', 'muhasebe']}><TeklifFatura /></ProtectedRoute>} />
            <Route path="/parca-esleme" element={<ProtectedRoute allowedRoles={['admin']}><ParcaEsleme /></ProtectedRoute>} />
            <Route path="/kullanici-yonetimi" element={<ProtectedRoute allowedRoles={['admin']}><KullaniciYonetimi /></ProtectedRoute>} />
            <Route path="/arama-filtre" element={<ProtectedRoute allowedRoles={['admin', 'satış', 'teknisyen']}><AramaFiltreleme /></ProtectedRoute>} />
            <Route path="/eposta-teklif" element={<ProtectedRoute allowedRoles={['admin', 'satış']}><EpostaTeklifGonder /></ProtectedRoute>} />
            <Route path="/parca-tahmin" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen']}><ParcaTahmin /></ProtectedRoute>} />
            <Route path="/kompresor-detay" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen']}><KompresorDetay /></ProtectedRoute>} />
            <Route path="/kompresor-kayitlari" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen']}><KompresorListesi /></ProtectedRoute>} />
            <Route path="/tedarikci-yonetimi" element={<ProtectedRoute allowedRoles={['admin']}><TedarikciYonetimi /></ProtectedRoute>} />
            <Route path="/parasut-faturalar" element={<ProtectedRoute allowedRoles={['admin', 'muhasebe']}><ParasutFaturaListesi /></ProtectedRoute>} />
            <Route path="/parasut-fatura-gonder" element={<ProtectedRoute allowedRoles={['admin', 'muhasebe']}><ParasutFaturaGonder /></ProtectedRoute>} />
            <Route path="/admin-panel" element={<ProtectedRoute allowedRoles={['admin']}><AdminPanel /></ProtectedRoute>} />
            <Route path="/teklif-basvurular" element={<ProtectedRoute allowedRoles={['admin', 'satış']}><TeklifBasvurular /></ProtectedRoute>} />
            <Route path="/compressor-yonetimi" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen']}><CompressorPartTracker /></ProtectedRoute>} />
            <Route path="/muadil-yonetim" element={<ProtectedRoute allowedRoles={['admin', 'teknisyen', 'satış']}><MuadilYonetim /></ProtectedRoute>} />
            <Route path="/kompresor-hesaplayici" element={
              <ProtectedRoute allowedRoles={['admin', 'satış', 'teknisyen']}>
                <KompresorHesaplayici />
              </ProtectedRoute>
            } />
            <Route path="/lupamat-kod-uretici" element={
              <ProtectedRoute allowedRoles={['admin', 'teknisyen', 'satış']}>
                <LupamatKodUretici />
              </ProtectedRoute>
            } />
            {/* ✅ Eski typo URL için redirect */}
            <Route path="/compreessor-yonetimi" element={<Navigate to="/compressor-yonetimi" replace />} />
          </Routes>

          {showFooter && <Footer />}
        </main>
      </div>
    </>
  );
};

// ✅ App — Loading state ile
function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#030712',
        color: '#94a3b8'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #a855f7',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <KompresorProvider>
        <TeklifProvider>
          <ThemeProvider>
            <Router>
              <SessionTimeout />
              <AppRoutes />
            </Router>
            {/* ✅ YENİ: Toast Notification Sistemi */}
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#1f2937',
                  color: '#f9fafb',
                  borderRadius: '8px',
                  fontSize: '14px',
                },
                success: {
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#f9fafb',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#f9fafb',
                  },
                },
              }}
            />
          </ThemeProvider>
        </TeklifProvider>
      </KompresorProvider>
    </AuthProvider>
  );
}

export default App;